import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { injectIntl } from 'react-intl';

import { fetchTaxonomies, fetchMetadataGroups } from '../../actions/Metadata';

import {
  APIupdateCustomCategoriesOrder,
  APIupdateMetadataGroupOrder,
} from '../../api/Metadata';

import { arrayMove } from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { userHasFeature } from '../../helpers/Features';

import AdminTaxonomiesHeader from '../../components/Admin/Taxonomies/Header';
import AdminTaxonomiesList from '../../components/Admin/Taxonomies/List';
import HelpIcon from '../../components/HelpIcon';
import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import EmptyCustomCategories from '../EmptyContainers/CustomCategories';
import TrialNotice from '../../components/TrialNotice';
import UpgradeBoard from '../../components/UpgradeBoard';

const AdminTaxonomies = ({
  intl,
  taxonomies: propsTaxonomies,
  metadataGroups: propsMetadataGroups,
  loadTaxonomies,
  loadMetadataGroups,
}) => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [metadataGroups, setMetadataGroups] = useState([]);
  const [groupOrders, setGroupOrders] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetadataGroups();
    loadTaxonomies();
  }, [loadTaxonomies, loadMetadataGroups]);

  useEffect(() => {
    setTaxonomies(propsTaxonomies);
    setLoading(false);

    setGroupOrders(prev => ({
      ...prev,
      ungrouped: propsTaxonomies.filter(t => t.metadata_groups?.length === 0).map(t => t.id),
      nogroups: propsTaxonomies.map(t => t.id),
    }));
  }, [propsTaxonomies]);

  useEffect(() => {
    setMetadataGroups(propsMetadataGroups);
    setLoading(false);

    setGroupOrders(prev => {
      const orders = { ...prev };
      propsMetadataGroups.forEach(metadataGroup => {
        orders[metadataGroup.id] = [...metadataGroup.taxonomies];
      });
      return orders;
    });
  }, [propsMetadataGroups]);

  const deleteTaxonomyFromList = useCallback((taxonomy) => {
    setTaxonomies(prev => prev.filter(t => t.id !== taxonomy.id));
    loadTaxonomies();
  }, [loadTaxonomies]);

  const moveCustomCategory = useCallback((dragId, hoverId, groupKey) => {
    setGroupOrders(prev => {
      const order = [...(prev[groupKey] || [])];
      const fmi = order.indexOf(dragId);
      const smi = order.indexOf(hoverId);
      if (fmi < 0 || smi < 0) return prev;
      const newOrder = arrayMove(order, fmi, smi);
      return { ...prev, [groupKey]: newOrder };
    });
  }, []);

  const saveCustomCategories = useCallback((metadataGroupId, groupKey) => {
    setGroupOrders(prev => {
      const order = prev[groupKey] || [];
      const request = metadataGroupId
        ? APIupdateMetadataGroupOrder(metadataGroupId, { taxonomies: order })
        : APIupdateCustomCategoriesOrder(order);

      request.then(() => {
        loadMetadataGroups();
        loadTaxonomies();
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Tag Categories order saved' }),
        });
      }).catch(() => {
        addFlashMessage({
          type: 'danger',
          text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
        });
      });

      return prev;
    });
  }, [loadTaxonomies, loadMetadataGroups, intl]);

  const hasCustomCategories =
    taxonomies.filter(t => !(t.ai_tagging || t.standard)).length > 0;

  const aiTaxonomies = taxonomies.filter(t => t.ai_tagging);

  const taxonomiesNoGroups = metadataGroups.length === 0
    ? (groupOrders.nogroups || []).map(id => taxonomies
      .filter(t => !t.ai_tagging).find(t => t.id === id)).filter(Boolean)
    : [];

  const taxonomiesWithoutGroups = metadataGroups.length > 0
    ? (groupOrders.ungrouped || []).map(id => taxonomies
      .filter(t => !t.ai_tagging).find(t => t.id === id)).filter(Boolean)
    : [];

  return (
    <div className="main-content taxonomies-content">
      <Container fluid>
        <AdminTaxonomiesHeader />
        <TrialNotice feature="custom-categories" className="pl-0" />
        <p>
          {intl.formatMessage({
            defaultMessage: 'Create and manage tags and Tag Categories. ',
          })}
          <HelpIcon
            id="help-custom-categories"
            url="https://help.stockpress.co/en/articles/5937851-creating-and-managing-custom-categories"
          />
        </p>

        {loading ? (
          <Loading />
        ) : (
          <ErrorBoundary>
            {aiTaxonomies.length > 0 && (
              <AdminTaxonomiesList
                ai={true}
                taxonomies={aiTaxonomies}
                taxonomiesTotal={aiTaxonomies.length}
                deleteTaxonomyFromList={deleteTaxonomyFromList}
                moveCustomCategory={(dragId, hoverId) => moveCustomCategory(dragId, hoverId, 'nogroups')}
                saveCustomCategories={() => saveCustomCategories(undefined, 'nogroups')}
              />
            )}
            {metadataGroups.length === 0 && (
              <AdminTaxonomiesList
                taxonomies={taxonomiesNoGroups}
                taxonomiesTotal={taxonomiesNoGroups.length}
                deleteTaxonomyFromList={deleteTaxonomyFromList}
                moveCustomCategory={(dragId, hoverId) => moveCustomCategory(dragId, hoverId, 'nogroups')}
                saveCustomCategories={() => saveCustomCategories(undefined, 'nogroups')}
              />
            )}

            {metadataGroups.length > 0 && (
              metadataGroups.map((metadataGroup) => {
                const order = groupOrders[metadataGroup.id] || metadataGroup.taxonomies;
                const groupTaxonomies = order
                  .map(id => taxonomies.find(t => t.id === id))
                  .filter(Boolean);

                return (
                  <AdminTaxonomiesList
                    key={metadataGroup.id}
                    metadataGroup={metadataGroup}
                    taxonomies={groupTaxonomies}
                    taxonomiesTotal={groupTaxonomies.length}
                    deleteTaxonomyFromList={deleteTaxonomyFromList}
                    moveCustomCategory={
                      (dragId, hoverId) => moveCustomCategory(dragId, hoverId, metadataGroup.id)
                    }
                    saveCustomCategories={
                      () => saveCustomCategories(metadataGroup.id, metadataGroup.id)
                    }
                  />
                );
              }))}

            {taxonomiesWithoutGroups.length > 0 && (
              <AdminTaxonomiesList
                metadataGroup={{ id: null, name: 'No Group assigned', taxonomies: taxonomiesWithoutGroups }}
                taxonomies={taxonomiesWithoutGroups}
                taxonomiesTotal={taxonomiesWithoutGroups.length}
                deleteTaxonomyFromList={deleteTaxonomyFromList}
                moveCustomCategory={(dragId, hoverId) => moveCustomCategory(dragId, hoverId, 'ungrouped')}
                saveCustomCategories={() => saveCustomCategories(undefined, 'ungrouped')}
              />
            )}

            {!hasCustomCategories && userHasFeature('custom-categories') && (
              <EmptyCustomCategories link="/admin/settings/categories/add" />
            )}
          </ErrorBoundary>
        )}

        {!userHasFeature('custom-categories') && (
          <UpgradeBoard feature="custom-categories" />
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
});

const mapDispatchToProps = dispatch => ({
  loadTaxonomies: () => dispatch(fetchTaxonomies()),
  loadMetadataGroups: () => dispatch(fetchMetadataGroups()),
});

AdminTaxonomies.propTypes = {
  intl: PropTypes.object.isRequired,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(AdminTaxonomies));
